import { getStores, hasSDK } from '@littlebird/sdk';
import {
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';

import { userStore } from '@/stores/user.store';
import { inElectron } from '@/utils/capabilities';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';
import { assertNever } from '@/utils/types';

import {
  getLocalTranscriptionDebugTraceOptions,
  getLocalTranscriptionUtteranceTraceOptions,
} from './local-transcription-debug';
import { setMeetingRecordingActive } from './meeting-activity';
import {
  convertDesktopAttendeesToStoreAttendees,
  storeMeetingToMeeting,
  updateStoreMeetingFromDesktopMeeting,
} from './meeting-mapper';
import {
  appendLiveTranscriptDelta,
  type LiveTranscriptChunk,
} from './meetings-live-transcript.utils';
import {
  buildTranscriptDisplayLines,
  buildTranscriptLines,
  type TranscriptDisplayLine,
  type TranscriptInterimChunk,
} from './meetings-transcript.utils';
import {
  applyUtteranceDeltaToView,
  createUtteranceProjectionState,
  flushUncommittedFinals,
  projectUtteranceChunks,
  type NewlyFinalizedUtterance,
  type UtteranceProjectionState,
  type UtteranceTranscriptDelta,
} from './meetings-utterance-projection';
import { ppDebug } from './meetings.debug-log';
import {
  createDurableMap,
  StoredFinalizedBoundarySchema,
  StoredInterruptedRecordingSchema,
  StoredPostProcessingJobSchema,
  type InterruptedRecordingRecord,
  type MeetingPostProcessingJobSourceState,
  type MeetingPostProcessingJobState,
  type RunningPostProcessingLedgerFields,
} from './meetings.durable-map';
import {
  type Meeting,
  type MeetingAudioEnergyLevel,
  type MeetingId,
  type MeetingPermissions,
  type MeetingSidebarPanel,
  type RecordingState,
  type StartMeetingInput,
  type TranscriptDeltaPayload,
} from './meetings.types';
/** Minimum energy level to count as speech (matches Swift's AudioActivity.speechThreshold) */
const SPEECH_AUDIO_THRESHOLD = 0.15;

const MEETING_MAC_PERMISSIONS_KEY = 'meeting-mac-permissions';
const MEETING_STATUS_WINDOW_VIEW_MODE_KEY = 'meeting-status-window-view-mode';
const MEETING_NOTES_INTRODUCED_KEY =
  'MEETING_NOTES_INTRODUCED_LOCAL_STORAGE_KEY';
const MEETING_ONBOARDING_CHECKLIST_COMPLETED_KEY =
  'meeting-onboarding-checklist-completed';
const MEETING_PREP_DISMISSED_KEY = 'meeting-prep-dismissed';
const MEETING_PREP_DISMISSED_MAX = 200;

// The ds: prefix keeps this key out of the logout localStorage wipe: pending
// transcripts survive sign-out and rehydrate only for their owning user.
export const MEETING_POST_PROCESSING_JOBS_KEY =
  'ds:meeting-post-processing-jobs';
export const MEETING_INTERRUPTED_RECORDINGS_KEY =
  'ds:meeting-interrupted-recordings';
export const MEETING_FINALIZED_BOUNDARIES_KEY =
  'ds:meeting-finalized-boundaries';

/** Entries are only consulted within a crash's resume grace period, so a cap
 * this size can never evict a boundary that is still needed. */
const MAX_FINALIZED_BOUNDARY_ENTRIES = 200;

const meetingIdsMatch = (
  leftMeetingId: string | undefined,
  rightMeetingId: string | undefined
) =>
  !!leftMeetingId &&
  !!rightMeetingId &&
  leftMeetingId.toLowerCase() === rightMeetingId.toLowerCase();

export const getMeeting = (meetingId?: MeetingId): Meeting | undefined => {
  if (!meetingId || !hasSDK()) return undefined;
  const storeMeeting = getStores().meetings.get(meetingId);
  if (!storeMeeting) return undefined;
  return storeMeetingToMeeting(storeMeeting) ?? undefined;
};

export const getMeetingByEventId = (eventId?: string): Meeting | undefined => {
  if (!eventId || !hasSDK()) return undefined;
  const storeMeeting = getStores().meetings.getMeetingByEventId(eventId);
  if (!storeMeeting) return undefined;
  return storeMeetingToMeeting(storeMeeting) ?? undefined;
};

export class MeetingStateManager {
  // Jobs survive restarts so recovery can pick them up: running jobs re-attach
  // their terminal-event handler on reconcile, while parked backend-upload
  // failures retain the completed transcript for upload retries.
  private postProcessingJobsLedger =
    createDurableMap<MeetingPostProcessingJobState>({
      storageKey: MEETING_POST_PROCESSING_JOBS_KEY,
      schema: StoredPostProcessingJobSchema,
      userScoped: true,
      debugLabel: 'post-processing jobs',
    });

  private interruptedRecordingsLedger =
    createDurableMap<InterruptedRecordingRecord>({
      storageKey: MEETING_INTERRUPTED_RECORDINGS_KEY,
      schema: StoredInterruptedRecordingSchema,
      userScoped: true,
      debugLabel: 'interrupted recordings',
    });

  /**
   * Persisted per-meeting last-finalized boundary (insertion order = eviction
   * order). Not user-scoped: meeting ids are UUIDs and a boundary is a fact
   * about the meeting's backend transcript, not user-actionable work, so it
   * hydrates once at startup rather than per signed-in user.
   */
  private finalizedBoundariesLedger = createDurableMap({
    storageKey: MEETING_FINALIZED_BOUNDARIES_KEY,
    schema: StoredFinalizedBoundarySchema,
    userScoped: false,
    debugLabel: 'finalized boundaries',
    maxEntries: MAX_FINALIZED_BOUNDARY_ENTRIES,
  });

  // Recording state
  recording: RecordingState = { status: 'idle' };
  recordingSessionAppBundleId: string | undefined = undefined;
  recordingSessionStartReason: StartMeetingInput['startReason'] | undefined =
    undefined;
  recordingSessionExpectsCall = false;
  energyLevel: MeetingAudioEnergyLevel = [0, 0, 0, 0, 0];
  lastTranscriptReceivedAt: Date | undefined = undefined;
  lastAudioReceivedAt: Date | undefined = undefined;
  currentTranscriptSpeaker: string | undefined = undefined;

  // Transcript state
  transcripts = observable.map<MeetingId, string>();
  private transcriptHandoffs = observable.map<MeetingId, symbol>();
  interimChunksByMeetingId = observable.map<MeetingId, LiveTranscriptChunk[]>();
  resolvedSpeakerNamesByMeetingId = observable.map<
    MeetingId,
    Map<string, string>
  >();
  postProcessingJobs = this.postProcessingJobsLedger.rows;
  interruptedRecordings = this.interruptedRecordingsLedger.rows;
  /**
   * Shallow map: the pure projection module replaces each value wholesale, so
   * deep observability would be wasted.
   */
  utteranceProjectionsByMeetingId = observable.map<
    MeetingId,
    UtteranceProjectionState
  >([], { deep: false });

  // UI state
  transcriptPopoverCloseRequest = 0;
  controlBarLoading = false;
  meetingInViewId: MeetingId | undefined = undefined;
  meetingStartedFromOverview = false;
  isStartingMeeting = false;
  statusWindowSyncCount = 0;
  sidebarPanels = observable.map<MeetingId, MeetingSidebarPanel>();
  prepPanelMeetingIds = observable.set<MeetingId>();
  finalizingSummaryMeetingIds = observable.set<MeetingId>();

  // Persisted state (localStorage)
  macPermissions: MeetingPermissions = getLocalStorageItem(
    MEETING_MAC_PERMISSIONS_KEY
  ) ?? {
    mic: false,
    speech: false,
  };

  statusWindowViewMode: 'transcripts' | 'notes' =
    getLocalStorageItem(MEETING_STATUS_WINDOW_VIEW_MODE_KEY) ?? 'transcripts';

  meetingNotesOnboarded: boolean =
    getLocalStorageItem(MEETING_NOTES_INTRODUCED_KEY) ?? false;

  onboardingChecklistCompleted: boolean =
    getLocalStorageItem(MEETING_ONBOARDING_CHECKLIST_COMPLETED_KEY) ?? false;

  prepDismissedMeetingIds = observable.set<MeetingId>(
    getLocalStorageItem(MEETING_PREP_DISMISSED_KEY) ?? []
  );

  guidedOnboardingMeetingId: MeetingId | null = null;
  guidedOnboardingStartPending = false;

  private hasInitializedOnboardingChecklistReaction = false;

  private stopSideEffects: (() => void) | null = null;

  constructor() {
    makeAutoObservable<
      this,
      | 'postProcessingJobsLedger'
      | 'interruptedRecordingsLedger'
      | 'finalizedBoundariesLedger'
      | 'stopSideEffects'
    >(
      this,
      {
        recording: observable.ref,
        postProcessingJobsLedger: false,
        interruptedRecordingsLedger: false,
        finalizedBoundariesLedger: false,
        stopSideEffects: false,
        start: false,
      },
      { autoBind: true }
    );

    this.finalizedBoundariesLedger.hydrateFromStorage();
    this.hydratePostProcessingJobsFromStorage();
    this.hydrateInterruptedRecordingsFromStorage();
  }

  start(): () => void {
    if (this.stopSideEffects) return this.stopSideEffects;

    // User restore is asynchronous; rehydrate on login, restore and account
    // switch. Logout empties memory while preserving every user's stored rows.
    const disposeUser = reaction(
      () => userStore.userId,
      () => {
        this.transcriptHandoffs.clear();
        this.hydratePostProcessingJobsFromStorage();
        this.hydrateInterruptedRecordingsFromStorage();
      },
      { fireImmediately: true }
    );
    // Derived here rather than at each write so no assignment path can miss it.
    const disposeRecording = reaction(
      () => this.recording.status !== 'idle',
      setMeetingRecordingActive,
      { fireImmediately: true }
    );
    let onboardingRetry: ReturnType<typeof setTimeout> | undefined;
    let disposeOnboarding: (() => void) | undefined;
    const startOnboarding = () => {
      if (!hasSDK()) {
        onboardingRetry = setTimeout(startOnboarding, 500);
        return;
      }
      runInAction(() => {
        this.hasInitializedOnboardingChecklistReaction = true;
      });
      // Auto-dismiss onboarding checklist when a completed summary exists
      disposeOnboarding = reaction(
        () => {
          if (this.onboardingChecklistCompleted) return false;
          return getStores().meetings.sortedMeetings.some(
            (m) => m.summaryStatus === 'completed' && !!m.summary
          );
        },
        (hasCompletedSummary) => {
          if (hasCompletedSummary) this.setOnboardingChecklistCompleted(true);
        },
        { fireImmediately: true }
      );
    };
    startOnboarding();

    const stop = () => {
      if (this.stopSideEffects !== stop) return;
      disposeUser();
      disposeRecording();
      disposeOnboarding?.();
      clearTimeout(onboardingRetry);
      runInAction(() => {
        this.hasInitializedOnboardingChecklistReaction = false;
      });
      this.stopSideEffects = null;
    };
    this.stopSideEffects = stop;
    return stop;
  }

  // Computed

  get asRecordingOrStoppingMeeting() {
    switch (this.recording.status) {
      case 'recording':
      case 'stopping':
        return this.recording;
      case 'idle':
      case 'loading':
        return null;
      default:
        assertNever(this.recording);
    }
  }

  get meetingInView() {
    if (!this.meetingInViewId) return null;
    return getMeeting(this.meetingInViewId);
  }

  get isBusy() {
    const r = this.recording;
    const meetingInView = getMeeting(this.meetingInViewId);
    const recordingMeeting = getMeeting(
      this.asRecordingOrStoppingMeeting?.meetingId
    );
    const summaryStatus =
      meetingInView?.summary_status ?? recordingMeeting?.summary_status;

    return (
      r.status === 'loading' ||
      r.status === 'stopping' ||
      summaryStatus === 'in_progress' ||
      summaryStatus === 'resummarizing'
    );
  }

  get hasMicrophonePermission(): boolean {
    if (!inElectron()) return true;
    return this.macPermissions.mic;
  }

  get shouldShowOnboardingChecklist(): boolean {
    if (!this.hasInitializedOnboardingChecklistReaction) return false;
    // Wait for meetings store to hydrate so the auto-dismiss reaction can
    // check for existing completed summaries before we show the checklist
    if (hasSDK() && !getStores().meetings.isLoaded) return false;
    return !this.onboardingChecklistCompleted;
  }

  get isGuidedOnboardingRecording(): boolean {
    return (
      this.guidedOnboardingStartPending || !!this.guidedOnboardingMeetingId
    );
  }

  isGuidedOnboardingMeeting(meetingId: MeetingId | undefined): boolean {
    if (!meetingId) return false;
    return this.guidedOnboardingMeetingId === meetingId;
  }

  isMeetingIdRecording(meetingId: string | undefined): boolean {
    const r = this.recording;
    return r.status === 'recording' && meetingIdsMatch(r.meetingId, meetingId);
  }

  isMeetingInRecordingLifecycle(meetingId: string | undefined): boolean {
    const recording = this.recording;
    return (
      'meetingId' in recording &&
      meetingIdsMatch(recording.meetingId, meetingId)
    );
  }

  isSummaryFinalizing(meetingId: MeetingId): boolean {
    return (
      this.finalizingSummaryMeetingIds.has(meetingId) ||
      this.interruptedRecordings.get(meetingId)?.deferSummary === true
    );
  }

  // Actions

  setSummaryFinalizing(meetingId: MeetingId, finalizing: boolean) {
    if (finalizing) this.finalizingSummaryMeetingIds.add(meetingId);
    else this.finalizingSummaryMeetingIds.delete(meetingId);
  }

  setRecording(state: RecordingState) {
    if (
      state.status !== 'idle' &&
      !this.isMeetingInRecordingLifecycle(state.meetingId) &&
      hasSDK()
    ) {
      const transcript = getStores().meetings.get(
        state.meetingId
      )?.personifiedTranscript;
      if (
        transcript !== undefined &&
        !this.transcriptHandoffs.has(state.meetingId)
      ) {
        this.transcripts.set(state.meetingId, transcript ?? '');
      }
    }
    this.recording = state;
  }

  setDurablePostProcessingJobState(state: MeetingPostProcessingJobSourceState) {
    return this.postProcessingJobsLedger.set({
      ...state,
      userId: userStore.userId,
    });
  }

  setPostProcessingJobRunning(
    meetingId: string,
    ledger: RunningPostProcessingLedgerFields
  ) {
    this.postProcessingJobsLedger.set({
      state: 'running',
      meetingId,
      startedAt: new Date().toISOString(),
      userId: userStore.userId,
      replaceFromOffset: ledger.replaceFromOffset,
      sessionEpoch: ledger.sessionEpoch,
      processingToken: ledger.processingToken,
      expectedLength: ledger.expectedLength,
      deferSummary: ledger.deferSummary ?? false,
    });
  }

  clearPostProcessingJob(meetingId: string) {
    this.postProcessingJobsLedger.remove(meetingId);
  }

  /** The ledger keys records by lowercased meeting id (every write path
   * normalizes); this is the matching case-insensitive read. */
  getPostProcessingJob(
    meetingId: string
  ): MeetingPostProcessingJobState | undefined {
    return this.postProcessingJobsLedger.get(meetingId);
  }

  /** Records where the last acked round left the backend transcript (code
   * points; null = settled length unknown → a later crash-resume keeps live).
   * Consulted when a resume consumes an interrupted record: the recording
   * cache then still holds every segment since this ack, so the next round
   * must replace from here, never from the current backend length. */
  setLastFinalizedBoundary(meetingId: string, boundary: number | null) {
    const normalizedMeetingId = meetingId.toLowerCase();
    const persisted = this.finalizedBoundariesLedger.set({
      meetingId: normalizedMeetingId,
      boundary,
    });
    ppDebug('finalized_boundary_set', {
      meetingId: normalizedMeetingId,
      boundary,
    });
    return persisted;
  }

  /** Null = unknown (an explicit unknown-length ack, or no entry because the
   * meeting predates boundary tracking); callers must treat null as unknown,
   * never as 0. */
  getLastFinalizedBoundary(meetingId: string): number | null {
    return this.finalizedBoundariesLedger.get(meetingId)?.boundary ?? null;
  }

  setInterruptedRecording(record: Omit<InterruptedRecordingRecord, 'userId'>) {
    const currentUserId = userStore.userId;
    // Without an owner we cannot stamp a durable record; nothing to park.
    if (!currentUserId) return;

    this.interruptedRecordingsLedger.set({ ...record, userId: currentUserId });
    ppDebug('interrupted_record_set', {
      meetingId: record.meetingId.toLowerCase(),
      reason: record.reason,
      interruptedAt: record.interruptedAt,
      usesLocalPostProcessing: record.usesLocalPostProcessing,
      promotionAttempts: record.promotionAttempts ?? 0,
    });
  }

  clearInterruptedRecording(meetingId: string) {
    const normalizedMeetingId = meetingId.toLowerCase();
    const existed =
      this.interruptedRecordingsLedger.remove(normalizedMeetingId);
    if (existed) {
      ppDebug('interrupted_record_cleared', { meetingId: normalizedMeetingId });
    }
  }

  /** Loads only the current user's records; other users' entries stay in
   * storage untouched until they sign back in. */
  hydrateInterruptedRecordingsFromStorage() {
    this.interruptedRecordingsLedger.hydrateFromStorage();
  }

  /** Logout hygiene, mirrors clearInMemoryPostProcessingJobs. */
  clearInMemoryInterruptedRecordings() {
    this.interruptedRecordingsLedger.clearInMemory();
  }

  /** Stamps a running record as having summarized the intact live transcript
   * after a retryable native failure, so reconcile does not re-summarize it. */
  markPostProcessingJobLiveSummarized(meetingId: string) {
    const existing = this.postProcessingJobsLedger.get(meetingId);
    if (existing?.state !== 'running') return;
    this.postProcessingJobsLedger.set({
      ...existing,
      liveSummarizedAt: new Date().toISOString(),
    });
  }

  /** Marks a retained round as safe for a user-triggered first summary. */
  markPostProcessingJobSummaryDeferredReady(meetingId: string) {
    const existing = this.postProcessingJobsLedger.get(meetingId);
    if (!existing) return;
    this.postProcessingJobsLedger.set({
      ...existing,
      summaryDeferredReadyAt: new Date().toISOString(),
    });
  }

  /** Loads only the current user's jobs; other users' entries stay in
   * storage untouched until they sign back in. */
  hydratePostProcessingJobsFromStorage() {
    this.postProcessingJobsLedger.hydrateFromStorage();
  }

  /** Logout hygiene: empties the in-memory map without touching storage, so
   * the next session cannot retry the previous user's uploads. */
  clearInMemoryPostProcessingJobs() {
    this.postProcessingJobsLedger.clearInMemory();
  }

  setRecordingSessionAppBundleId(appBundleId: string | undefined) {
    this.recordingSessionAppBundleId = appBundleId;
  }

  setRecordingSessionStartReason(
    reason: StartMeetingInput['startReason'] | undefined
  ) {
    this.recordingSessionStartReason = reason;
  }

  setRecordingSessionExpectsCall(expectsCall: boolean) {
    this.recordingSessionExpectsCall = expectsCall;
  }

  setEnergyLevel(level: MeetingAudioEnergyLevel) {
    this.energyLevel = [...level];
    if (
      this.recording.status === 'recording' &&
      level.some((l) => l > SPEECH_AUDIO_THRESHOLD)
    ) {
      this.lastAudioReceivedAt = new Date();
    }
  }

  setMacPermissions(permissions: MeetingPermissions) {
    this.macPermissions = permissions;
    setLocalStorageItem(MEETING_MAC_PERMISSIONS_KEY, permissions);
  }

  setStatusWindowViewMode(mode: 'transcripts' | 'notes') {
    this.statusWindowViewMode = mode;
    setLocalStorageItem(MEETING_STATUS_WINDOW_VIEW_MODE_KEY, mode);
  }

  setIsStartingMeeting(value: boolean) {
    this.isStartingMeeting = value;
  }

  setMeetingNotesOnboarded(value: boolean) {
    this.meetingNotesOnboarded = value;
    setLocalStorageItem(MEETING_NOTES_INTRODUCED_KEY, value);
  }

  isPrepDismissed(meetingId: MeetingId): boolean {
    return this.prepDismissedMeetingIds.has(meetingId);
  }

  dismissPrep(meetingId: MeetingId) {
    this.prepDismissedMeetingIds.add(meetingId);
    while (this.prepDismissedMeetingIds.size > MEETING_PREP_DISMISSED_MAX) {
      const oldest = this.prepDismissedMeetingIds.values().next().value;
      if (oldest === undefined) break;
      this.prepDismissedMeetingIds.delete(oldest);
    }
    setLocalStorageItem(MEETING_PREP_DISMISSED_KEY, [
      ...this.prepDismissedMeetingIds,
    ]);
  }

  clearPrepDismissed(meetingId: MeetingId) {
    if (!this.prepDismissedMeetingIds.delete(meetingId)) return;
    setLocalStorageItem(MEETING_PREP_DISMISSED_KEY, [
      ...this.prepDismissedMeetingIds,
    ]);
  }

  isPrepPanelOpen(meetingId: MeetingId): boolean {
    return this.prepPanelMeetingIds.has(meetingId);
  }

  openPrepPanel(meetingId: MeetingId) {
    this.clearPrepDismissed(meetingId);
    this.prepPanelMeetingIds.add(meetingId);
    const storeMeeting = hasSDK()
      ? getStores().meetings.get(meetingId)
      : undefined;
    if (storeMeeting?.prepContentCollapsed)
      storeMeeting.togglePrepContentCollapsed();
  }

  closePrepPanel(meetingId: MeetingId) {
    this.prepPanelMeetingIds.delete(meetingId);
  }

  setOnboardingChecklistCompleted(value: boolean) {
    this.onboardingChecklistCompleted = value;
    setLocalStorageItem(MEETING_ONBOARDING_CHECKLIST_COMPLETED_KEY, value);
  }

  setIsGuidedOnboardingRecording(value: boolean, meetingId?: MeetingId) {
    if (!value) {
      this.guidedOnboardingStartPending = false;
      this.guidedOnboardingMeetingId = null;
      return;
    }

    if (meetingId) {
      this.guidedOnboardingStartPending = false;
      this.guidedOnboardingMeetingId = meetingId;
      return;
    }

    this.guidedOnboardingStartPending = true;
    this.guidedOnboardingMeetingId = null;
  }

  // SDK store mutations

  setMeeting(meeting: Partial<Meeting> & { id: string }) {
    if (!hasSDK()) return;

    const store = getStores().meetings;
    const existing = store.get(meeting.id);

    if (existing) {
      updateStoreMeetingFromDesktopMeeting(existing, meeting);
      return;
    }

    store.add({
      id: meeting.id,
      name: meeting.name ?? null,
      summary: meeting.summary ?? null,
      tldr: meeting.tldr ?? null,
      personifiedTranscript: meeting.personified_transcript ?? null,
      notes: meeting.notes ?? null,
      prep: meeting.prep ?? null,
      sourceApp: meeting.source_app ?? null,
      eventId: meeting.event_id ?? null,
      eventStartAt: meeting.event_start_at
        ? new Date(meeting.event_start_at)
        : null,
      projectId: meeting.project_id ?? null,
      status: meeting.status ?? 'active',
      summaryStatus: meeting.summary_status ?? 'initial',
      attendees: convertDesktopAttendeesToStoreAttendees(meeting.attendees),
      speakers: meeting.speakers ?? null,
      endedAt: meeting.ended_at ? new Date(meeting.ended_at) : null,
      firstChunkTimestamp: meeting.first_chunk_timestamp
        ? new Date(meeting.first_chunk_timestamp)
        : null,
      lastTranscribedAt: meeting.last_transcribed_at
        ? new Date(meeting.last_transcribed_at)
        : null,
      firstTranscriptAt: meeting.first_transcript_at
        ? new Date(meeting.first_transcript_at)
        : null,
      transcriptDeletedAt: meeting.transcript_deleted_at
        ? new Date(meeting.transcript_deleted_at)
        : null,
      createdAt: meeting.created_at ? new Date(meeting.created_at) : new Date(),
      updatedAt: meeting.updated_at ? new Date(meeting.updated_at) : undefined,
    });
  }

  setMeetings(meetings: Meeting[]) {
    for (const meeting of meetings) {
      this.setMeeting(meeting);
    }
  }

  removeMeeting(meetingId: MeetingId) {
    if (hasSDK()) {
      getStores().meetings.removeLocally(meetingId);
    }
    this.sidebarPanels.delete(meetingId);
  }

  // Recording lifecycle

  resetRecordingState() {
    this.recording = { status: 'idle' };
    this.recordingSessionAppBundleId = undefined;
    this.recordingSessionStartReason = undefined;
    this.recordingSessionExpectsCall = false;
    this.lastTranscriptReceivedAt = undefined;
    this.lastAudioReceivedAt = undefined;
    this.interimChunksByMeetingId.clear();
    this.commitPendingUtteranceFinals();
    this.utteranceProjectionsByMeetingId.clear();
    this.currentTranscriptSpeaker = undefined;
  }

  /**
   * Finals still queued behind an utterance that never closed would otherwise
   * be dropped with the projection, leaving the on-screen transcript short
   * until the post-processed one lands.
   */
  private commitPendingUtteranceFinals() {
    for (const [meetingId, projection] of this
      .utteranceProjectionsByMeetingId) {
      const view = flushUncommittedFinals({
        projection,
        transcript: this.transcripts.get(meetingId) ?? '',
        currentSpeaker: this.currentTranscriptSpeaker,
      });
      this.transcripts.set(meetingId, view.transcript);
    }
  }

  // Transcript operations

  retainTranscriptUntilBootstrap(meetingId: MeetingId): () => boolean {
    const handoff = Symbol(meetingId);
    this.transcriptHandoffs.set(meetingId, handoff);
    return () =>
      runInAction(() => {
        if (this.transcriptHandoffs.get(meetingId) !== handoff) return false;
        this.transcriptHandoffs.delete(meetingId);
        if (this.isMeetingInRecordingLifecycle(meetingId)) return false;
        this.transcripts.delete(meetingId);
        return true;
      });
  }

  initializeTranscript(meetingId: MeetingId): string {
    const existing = this.transcripts.get(meetingId);
    if (existing !== undefined) return existing;

    const transcript = hasSDK()
      ? (getStores().meetings.get(meetingId)?.personifiedTranscript ?? '')
      : '';

    this.transcripts.set(meetingId, transcript);
    return transcript;
  }

  /**
   * Replaces the committed transcript shown in the UI. Used after local
   * post-processing finalizes a replace on the backend: the in-session live
   * copy cached in `transcripts` (or a pre-replace copy a bootstrap synced)
   * would otherwise shadow the post-processed text until an app restart.
   * Skips a meeting that is recording again — that live session owns the
   * on-screen transcript, so a finalization racing a resume must not blank it.
   */
  setCommittedTranscript(meetingId: MeetingId, transcript: string): void {
    if (this.isMeetingIdRecording(meetingId)) return;
    this.transcripts.set(meetingId, transcript);
  }

  appendTranscriptDelta(
    meetingId: MeetingId,
    delta: TranscriptDeltaPayload
  ): void {
    const currentTranscript = this.initializeTranscript(meetingId);
    this.lastTranscriptReceivedAt = new Date();
    const nextState = appendLiveTranscriptDelta(
      {
        transcript: currentTranscript,
        interimChunks: this.interimChunksByMeetingId.get(meetingId) ?? [],
        currentSpeaker: this.currentTranscriptSpeaker,
      },
      delta,
      {
        // DevTools: localStorage.setItem('localTranscriptionDebug', 'true')
        ...getLocalTranscriptionDebugTraceOptions('main-window'),
      }
    );

    this.currentTranscriptSpeaker = nextState.currentSpeaker;
    this.transcripts.set(meetingId, nextState.transcript);

    if (nextState.interimChunks.length > 0) {
      this.interimChunksByMeetingId.set(meetingId, nextState.interimChunks);
    } else {
      this.interimChunksByMeetingId.delete(meetingId);
    }

    if (!delta.isFinal || !hasSDK()) {
      return;
    }

    const sdkMeeting = getStores().meetings.get(meetingId);
    if (sdkMeeting) {
      sdkMeeting.lastTranscribedAt = new Date();
    }
  }

  /**
   * Handles deltas carrying `utteranceId` (the local engine's only emission
   * shape). Returns `newlyFinalized` so the caller syncs the backend exactly
   * once per utterance. Already a MobX action via `makeAutoObservable`, so no
   * explicit `runInAction` is needed.
   */
  applyUtteranceTranscriptDelta(
    meetingId: MeetingId,
    delta: UtteranceTranscriptDelta
  ): NewlyFinalizedUtterance | undefined {
    const currentTranscript = this.initializeTranscript(meetingId);
    this.lastTranscriptReceivedAt = new Date();

    const { view, newlyFinalized } = applyUtteranceDeltaToView(
      {
        projection:
          this.utteranceProjectionsByMeetingId.get(meetingId) ??
          createUtteranceProjectionState(),
        transcript: currentTranscript,
        currentSpeaker: this.currentTranscriptSpeaker,
      },
      delta,
      {
        // DevTools: localStorage.setItem('localTranscriptionDebug', 'true')
        ...getLocalTranscriptionUtteranceTraceOptions('main-window'),
      }
    );
    this.utteranceProjectionsByMeetingId.set(meetingId, view.projection);

    if (!newlyFinalized) {
      return undefined;
    }

    this.transcripts.set(meetingId, view.transcript);
    this.currentTranscriptSpeaker = view.currentSpeaker;

    if (hasSDK()) {
      const sdkMeeting = getStores().meetings.get(meetingId);
      if (sdkMeeting) {
        sdkMeeting.lastTranscribedAt = new Date();
      }
    }

    return newlyFinalized;
  }

  /**
   * Concatenates legacy interim chunks (remote engine) with live projected
   * utterances; on the utterance path the legacy list is always empty because
   * routing keeps the two paths disjoint.
   */
  getInterimChunks(meetingId: MeetingId): TranscriptInterimChunk[] {
    const legacyChunks = this.interimChunksByMeetingId.get(meetingId) ?? [];
    const projection = this.utteranceProjectionsByMeetingId.get(meetingId);
    if (!projection) {
      return legacyChunks;
    }
    return [...legacyChunks, ...projectUtteranceChunks(projection)];
  }

  getCommittedTranscript(meetingId: MeetingId): string {
    const stored = hasSDK()
      ? getStores().meetings.get(meetingId)?.personifiedTranscript
      : undefined;
    if (
      !this.isMeetingInRecordingLifecycle(meetingId) &&
      !this.transcriptHandoffs.has(meetingId) &&
      stored !== undefined
    ) {
      return stored ?? '';
    }
    return this.transcripts.get(meetingId) ?? stored ?? '';
  }

  getTranscriptLines(meetingId: MeetingId | undefined): string[] {
    if (!meetingId) return [];

    return buildTranscriptLines(
      this.getCommittedTranscript(meetingId),
      this.getInterimChunks(meetingId)
    );
  }

  setResolvedSpeakerName(
    meetingId: MeetingId,
    diarizedLabel: string,
    speakerName: string
  ): void {
    const names =
      this.resolvedSpeakerNamesByMeetingId.get(meetingId) ??
      new Map<string, string>();
    names.set(diarizedLabel, speakerName);
    this.resolvedSpeakerNamesByMeetingId.set(meetingId, names);
  }

  getResolvedSpeakerNames(
    meetingId: MeetingId | undefined
  ): ReadonlyMap<string, string> | undefined {
    if (!meetingId) return undefined;
    return this.resolvedSpeakerNamesByMeetingId.get(meetingId);
  }

  renameResolvedSpeaker(
    meetingId: MeetingId,
    currentLabel: string,
    newLabel: string
  ): void {
    const names =
      this.resolvedSpeakerNamesByMeetingId.get(meetingId) ??
      new Map<string, string>();
    for (const [label, name] of names) {
      if (name === currentLabel) names.set(label, newLabel);
    }
    names.set(currentLabel, newLabel);
    this.resolvedSpeakerNamesByMeetingId.set(meetingId, names);
  }

  getTranscriptDisplayLines(
    meetingId: MeetingId | undefined
  ): TranscriptDisplayLine[] {
    if (!meetingId) return [];

    return buildTranscriptDisplayLines(
      this.getCommittedTranscript(meetingId),
      this.getInterimChunks(meetingId)
    );
  }

  isPrepState(meetingId: MeetingId | undefined): boolean {
    if (!meetingId) return false;
    const meeting = getMeeting(meetingId);
    if (!meeting) return false;

    return (
      (!meeting.summary_status ||
        meeting.summary_status === 'initial' ||
        meeting.summary_status === 'no_transcript') &&
      !this.isMeetingIdRecording(meetingId) &&
      this.getTranscriptLines(meetingId).length === 0
    );
  }

  // UI helpers

  closeTranscriptPopover() {
    this.transcriptPopoverCloseRequest += 1;
  }

  openSidebar(meetingId: MeetingId, panel: 'chat') {
    this.sidebarPanels.set(meetingId, panel);
  }

  closeSidebar(meetingId: MeetingId) {
    this.sidebarPanels.set(meetingId, null);
  }

  // Subscriptions (for non-React consumers)

  subscribeAudioEnergyLevel(
    cb: (energyLevel: MeetingAudioEnergyLevel) => void
  ): () => void {
    return reaction(
      () => this.energyLevel,
      (energyLevel) => cb(energyLevel)
    );
  }

  subscribeMeetingPropertyChanges(
    properties: Array<keyof Meeting>,
    cb: (patches: Array<{ id: MeetingId; [key: string]: unknown }>) => void
  ): () => void {
    if (!hasSDK()) return () => {};

    const store = getStores().meetings;

    const propMap: Record<string, string> = {
      personified_transcript: 'personifiedTranscript',
      summary_status: 'summaryStatus',
      source_app: 'sourceApp',
      event_id: 'eventId',
      ended_at: 'endedAt',
      first_chunk_timestamp: 'firstChunkTimestamp',
      last_transcribed_at: 'lastTranscribedAt',
      first_transcript_at: 'firstTranscriptAt',
      created_at: 'createdAt',
      updated_at: 'updatedAt',
    };

    const dateFields = new Set([
      'created_at',
      'updated_at',
      'ended_at',
      'first_chunk_timestamp',
      'last_transcribed_at',
      'first_transcript_at',
    ]);

    // Store raw observable references for cheap === comparison.
    // toJS() is only called when building outgoing patches.
    const previousRefs = new Map<MeetingId, Record<string, unknown>>();

    return reaction(
      () => {
        const snapshot: Record<string, Record<string, unknown>> = {};
        for (const [id, meeting] of store.data.entries()) {
          snapshot[id] = {};
          for (const prop of properties) {
            const propKey = prop as string;
            const modelProp = propMap[propKey] ?? propKey;
            const value = (meeting as unknown as Record<string, unknown>)[
              modelProp
            ];
            // Keep the raw observable reference — MobX still tracks it,
            // and the reference is stable until the value actually changes
            snapshot[id][propKey] = value;
          }
        }
        return snapshot;
      },
      (currentSnapshot: Record<string, Record<string, unknown>>) => {
        const currentIds = Object.keys(currentSnapshot);
        const previousIds = Array.from(previousRefs.keys());

        const deletedIds = previousIds.filter((id) => !currentIds.includes(id));
        if (deletedIds.length > 0) {
          deletedIds.forEach((id) => previousRefs.delete(id));
          cb(deletedIds.map((id) => ({ id, deleted: true })));
          return;
        }

        const patches: Array<{ id: MeetingId; [key: string]: unknown }> = [];

        for (const [id, props] of Object.entries(currentSnapshot)) {
          const prev = previousRefs.get(id);

          if (!prev) {
            previousRefs.set(id, { ...props });
            continue;
          }

          const patch: { id: MeetingId; [key: string]: unknown } = { id };
          let hasChanges = false;

          for (const prop of properties) {
            const propKey = prop as string;
            if (prev[propKey] !== props[propKey]) {
              // Convert to plain JS for the outgoing patch
              let value = props[propKey];
              if (dateFields.has(propKey) && value instanceof Date) {
                value = value.toISOString();
              } else if (value !== null && typeof value === 'object') {
                value = toJS(value);
              }
              patch[propKey] = value;
              hasChanges = true;
            }
          }

          if (hasChanges) {
            previousRefs.set(id, { ...props });
            patches.push(patch);
          }
        }

        if (patches.length > 0) {
          cb(patches);
        }
      }
    );
  }
}

export const meetingStore = new MeetingStateManager();
meetingStore.start();
